/**
 * What a pass launched, in the two units it really has (CU-869fm5ju2 § 3.4).
 *
 *   searches     — `run.composed` (`searches_composed`): office × pickup
 *                  date, the billed unit;
 *   price points — `run.created` (`searches_created`, rows of NEW_SEARCH):
 *                  office × pickup date × rental length × site. The
 *                  progress (`run.searches`) counts the same rows.
 *
 * They differ in nearly every pass — a search carries several price
 * points — so a difference is not a problem. The amber mark is for real
 * trouble only: sites the launch path dropped (no scraper queue;
 * `skipped.discarded_by_launch_path` counts sites, not price points) and
 * search POSTs that failed (`error_message`).
 *
 * Every field read from webback / the engine is named once, in `F` and `R`.
 */
import { fmtInt } from '../../Page/@services/cadence.js'
import { pricePointsLabel, searchesLabel } from '../../Page/@services/runNow.js'

/** Executions' item fields (webback `list_executions`). */
const F = {
	searches: 'composed',
	pricePoints: 'created',
	skipped: 'skipped',
	droppedSites: 'discarded_by_launch_path',
	error: 'error_message',
	progress: 'searches',
}

/** The engine's force report (Run now · all). */
const R = { searches: 'composed', pricePoints: 'tasks', ran: 'ran', launched: 'launched', why: 'reason' }

/** The PMS push summary (webback `_push_state`): rows that reached the engine, of those created. */
const P = { arrived: 'tasks_arrived', expected: 'tasks_expected' }

const num = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v))

/** `{ searches, pricePoints, label, warning }` — `warning` is the amber tooltip, or null. */
export function passCounts(run) {
	const searches = num(run?.[F.searches]) || 0
	const pricePoints = num(run?.[F.pricePoints]) || 0
	const dropped = num(run?.[F.skipped]?.[F.droppedSites]) || 0
	const why = []
	if (dropped > 0) why.push(`${fmtInt(dropped)} site${dropped === 1 ? '' : 's'} dropped by the launch path (no scraper queue)`)
	if (run?.[F.error]) why.push(String(run[F.error]))
	return {
		searches,
		pricePoints,
		label: `${searchesLabel(searches)} · ${pricePointsLabel(pricePoints)}`,
		warning: why.length ? why.join(' · ') : null,
	}
}

/** `{ done, total, pct, label }` — "X of Y price points · N running · N failed". */
export function passProgress(run) {
	const counts = run?.[F.progress] || {}
	const done = (counts.finished || 0) + (counts.error || 0) + (counts['not-avail'] || 0)
	const total = Object.values(counts).reduce((a, b) => a + b, 0) || num(run?.[F.pricePoints]) || 0
	const pct = total ? Math.round((done / total) * 100) : 0
	const label = `${fmtInt(done)} of ${pricePointsLabel(total)}`
		+ (counts.running ? ` · ${fmtInt(counts.running)} running` : '')
		+ (counts.error ? ` · ${fmtInt(counts.error)} failed` : '')
	return { done, total, pct, label }
}

/** The toast of Run now · all strategies on, from the engine's report. */
export function launchedAllToast(report) {
	const searches = num(report?.[R.searches])
	if (searches == null) return 'Pass launched.'
	const pricePoints = num(report?.[R.pricePoints])
	return `Launched ${searchesLabel(searches)}${pricePoints != null ? ` · ${pricePointsLabel(pricePoints)}` : ''}.`
}

/** `{ ok, text }` for Run now · all: never "launched" when the engine launched nothing. */
export function launchAllOutcome(report) {
	if (report?.[R.ran] === false || report?.[R.launched] === false) {
		const why = report?.[R.why]
		return { ok: false, text: `Nothing launched${why ? `: ${why}` : ''}.` }
	}
	return { ok: true, text: launchedAllToast(report) }
}

/** The PMS push line: "X of Y price points" (CU-869fm5ju2 D7 — the engine calls them tasks). */
export function pushArrivedLabel(push) {
	return `${fmtInt(num(push?.[P.arrived]) || 0)} of ${pricePointsLabel(num(push?.[P.expected]) || 0)}`
}

/**
 * What the Decisions cell can honestly say (CU-869fmm943): `unavailable`
 * when ClickHouse could not be read (`decisions_ok: false`), `none` when
 * the engine decided nothing yet, `chips` otherwise. Until then a failed
 * read came back as an empty map and every pass said "nothing yet".
 * A missing flag (an older server) reads as ok.
 */
export function decisionsState(run, decisionsOk) {
	if (decisionsOk === false) return { kind: 'unavailable', total: 0 }
	const total = Object.values(run?.decisions || {}).reduce((a, b) => a + (Number(b) || 0), 0)
	return { kind: total ? 'chips' : 'none', total }
}
